import { Input } from "@/app/admin/_common/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import type { FormRequest } from "@/app/_services/contactService";
import { STATUS_LABELS, FORM_TYPE_LABELS } from "./requestLabels";
import type { StatusFilter, FormTypeFilter } from "../requestsPageModel";
import { Search, ChevronDown } from "lucide-react";

function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  const activeLabel = options.find((o) => o.value === value)?.label ?? label;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-[30px] items-center gap-1.5 rounded-md border border-input bg-white px-3 text-[12.5px] text-foreground hover:bg-muted"
        >
          <span className="hidden text-muted-foreground sm:inline">{label}:</span>
          {activeLabel}
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as T)}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function RequestsToolbar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  formTypeFilter,
  onFormTypeFilterChange,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (value: StatusFilter) => void;
  formTypeFilter: FormTypeFilter;
  onFormTypeFilterChange: (value: FormTypeFilter) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative max-w-[420px] flex-1">
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <Search className="size-3.5 text-muted-foreground" />
        </div>
        <Input
          type="search"
          placeholder="Search name…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-[30px] pl-9 text-[12.5px]"
          aria-label="Search requests"
        />
      </div>

      <FilterDropdown<StatusFilter>
        label="Status"
        value={statusFilter}
        onChange={onStatusFilterChange}
        options={[
          { value: "all", label: "All" },
          ...(Object.entries(STATUS_LABELS) as [FormRequest["status"], string][]).map(
            ([value, label]) => ({ value, label })
          ),
        ]}
      />

      <FilterDropdown<FormTypeFilter>
        label="Form Type"
        value={formTypeFilter}
        onChange={onFormTypeFilterChange}
        options={[
          { value: "all", label: "All" },
          ...(Object.entries(FORM_TYPE_LABELS) as [FormRequest["form_type"], string][]).map(
            ([value, label]) => ({ value, label })
          ),
        ]}
      />
    </div>
  );
}
